/**
 * Endpoints de despacho — mutaciones (confirmar, cancelar).
 *
 * El back real implementa estas operaciones server-side dentro de una
 * transacción (la mutación es atómica: serie no puede quedar "en_transito"
 * sin operación asociada). El mock las hace sincrónicas sobre el in-memory
 * db y simula la respuesta del SNT con un setTimeout (2s).
 */

import { z } from 'zod';
import { OperacionSchema } from '@/api/contracts/operaciones';
import { getDb, nextMovId, nextOpId } from '@/api/mock/db';
import { logApi } from '@/stores/apilog';
import { ApiError } from './auth';

const MOCK_LATENCY_MS = 150;
function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// =============================================================================
// CONFIRMAR DESPACHO
// =============================================================================

export const ConfirmarDespachoRequest = z.object({
  remitoNumero: z.string().min(1),
  /** Tipo de operación según destinoTipo del remito. */
  tipo: z.enum(['despacho_transferencia', 'despacho_venta', 'despacho_destruccion']),
  ubicacionId: z.string().min(1),
  usuarioId: z.string().min(1),
  eventoAnmatId: z.number().int().positive(),
  /** IDs de las series escaneadas (en orden). */
  seriesIds: z.array(z.string().min(1)).min(1),
});

export const ConfirmarDespachoResponse = z.object({
  operacion: OperacionSchema,
});

export type ConfirmarDespachoRequest = z.infer<typeof ConfirmarDespachoRequest>;
export type ConfirmarDespachoResponse = z.infer<typeof ConfirmarDespachoResponse>;

export async function confirmarDespacho(
  input: ConfirmarDespachoRequest,
): Promise<ConfirmarDespachoResponse> {
  const req = ConfirmarDespachoRequest.parse(input);
  await delay(MOCK_LATENCY_MS);

  const db = getDb();
  const remito = db.remitos.find((r) => r.numero === req.remitoNumero);
  if (!remito) throw new ApiError(`Remito ${req.remitoNumero} no encontrado`, 'REMITO_NOT_FOUND');
  if (remito.estadoEnPlataforma !== 'pendiente_trazar') {
    throw new ApiError(`Remito ${req.remitoNumero} no está pendiente de trazar`, 'REMITO_NOT_PENDING');
  }

  // Validar todas las series existen + están disponibles en la ubicación origen
  const seriesObjs = req.seriesIds.map((sid) => db.series.find((s) => s.id === sid));
  const faltantes = req.seriesIds.filter((_sid, i) => !seriesObjs[i]);
  if (faltantes.length > 0) {
    throw new ApiError(`Series no encontradas: ${faltantes.join(', ')}`, 'SERIES_NOT_FOUND');
  }
  const malUbicadas = seriesObjs.filter(
    (s) => s && (s.ubicacionActualId !== req.ubicacionId || s.tenencia !== 'en_poder'),
  );
  if (malUbicadas.length > 0) {
    throw new ApiError(
      `${malUbicadas.length} series no están disponibles en la ubicación`,
      'SERIES_UNAVAILABLE',
    );
  }

  // Crear la operación con anmat.estado = 'en_cola'
  const ahora = new Date().toISOString();
  const opId = nextOpId();
  const tipoMensaje: 'informarTransaccion' | 'informarDestruccion' =
    req.tipo === 'despacho_destruccion' ? 'informarDestruccion' : 'informarTransaccion';
  const operacion = {
    id: opId,
    tipo: req.tipo,
    ubicacionId: req.ubicacionId,
    // Propagar el destino para que Recepción pueda filtrar por GLN.
    // Solo aplica a transferencias entre ubicaciones internas; en venta y
    // destrucción queda undefined (no se recepciona acá).
    ...(req.tipo === 'despacho_transferencia' && remito.destinoUbicacionId
      ? { destinoUbicacionId: remito.destinoUbicacionId }
      : {}),
    remitoNumero: req.remitoNumero,
    eventoAnmatId: req.eventoAnmatId,
    fecha: ahora,
    usuarioId: req.usuarioId,
    seriesIds: [...req.seriesIds],
    estado: 'cerrada' as const,
    anmat: {
      tipoMensaje,
      estado: 'en_cola' as const,
      intentos: 0,
      codigoTransaccion: null,
      mensajes: [
        { ts: ahora, tipo: 'request' as const, body: `${tipoMensaje} enviado al SNT` },
      ],
    },
    pdfGeneradoAt: null,
  };
  db.operaciones.push(operacion);

  // Actualizar tenencia de series a en_transito + agregar movimiento por cada una
  for (const sid of req.seriesIds) {
    const serie = db.series.find((s) => s.id === sid);
    if (!serie) continue;
    serie.tenencia = 'en_transito';
    db.movimientos.push({
      id: nextMovId(),
      serieId: sid,
      operacionId: opId,
      tipoEvento: req.tipo,
      fecha: ahora,
      ubicacionId: req.ubicacionId,
      usuarioId: req.usuarioId,
      codigoTransaccion: null,
      eventoAnmatId: req.eventoAnmatId,
      detalle: `Despacho · remito ${req.remitoNumero}`,
    });
  }

  // Marcar el remito como trazado
  remito.estadoEnPlataforma = 'trazado';

  // (v13) Loggear el envío al SNT en el API log
  logApi({
    sistema: 'snt',
    metodo: 'POST',
    endpoint: `/snt/${tipoMensaje}`,
    status: 200,
    operacionId: opId,
    requestBody: {
      tipoMensaje,
      eventoAnmatId: req.eventoAnmatId,
      cantSeries: req.seriesIds.length,
    },
    responseBody: { estado: 'aceptado_en_cola', cola: true },
  });

  // Simular respuesta del SNT (2s) — 90% éxito, 10% error tipificado
  setTimeout(() => {
    const op = db.operaciones.find((o) => o.id === opId);
    if (!op) return;
    // FIX CR-1: si la op ya fue cancelada en los 2s, NO pisamos.
    if (op.anmat.estado === 'cancelada') return;
    const success = Math.random() > 0.1;
    if (success) {
      const codigoTx = 'TX-' + Math.random().toString(36).slice(2, 10).toUpperCase();
      op.anmat.estado = 'informada';
      op.anmat.codigoTransaccion = codigoTx;
      op.anmat.mensajes.push({
        ts: new Date().toISOString(),
        tipo: 'response',
        body: `OK · ${codigoTx}`,
      });
      // Propagar código a los movimientos
      for (const m of db.movimientos) {
        if (m.operacionId === opId && !m.codigoTransaccion) m.codigoTransaccion = codigoTx;
      }
      logApi({
        sistema: 'snt',
        metodo: 'POST',
        endpoint: `/snt/${tipoMensaje}/callback`,
        status: 200,
        operacionId: opId,
        responseBody: { codigoTransaccion: codigoTx, estado: op.anmat.estado },
      });
    } else {
      op.anmat.estado = 'error';
      op.anmat.intentos = (op.anmat.intentos ?? 0) + 1;
      op.anmat.ultimoError = {
        code: 'E099',
        msg: 'Error simulado del SNT',
        severidad: 'medio',
        ts: new Date().toISOString(),
      };
      op.anmat.mensajes.push({
        ts: new Date().toISOString(),
        tipo: 'response',
        body: 'Error E099: Error simulado del SNT',
      });
      logApi({
        sistema: 'snt',
        metodo: 'POST',
        endpoint: `/snt/${tipoMensaje}/callback`,
        status: 400,
        operacionId: opId,
        errorMsg: 'E099: Error simulado del SNT',
      });
    }
  }, 2000);

  return ConfirmarDespachoResponse.parse({ operacion });
}

// =============================================================================
// CANCELAR TRANSACCIÓN
// =============================================================================

export const CancelarTransaccionRequest = z.object({
  operacionId: z.string().min(1),
  usuarioId: z.string().min(1),
  razon: z.string().min(1, 'Razón obligatoria para cancelar'),
});

export const CancelarTransaccionResponse = z.object({
  operacion: OperacionSchema,
});

export type CancelarTransaccionRequest = z.infer<typeof CancelarTransaccionRequest>;
export type CancelarTransaccionResponse = z.infer<typeof CancelarTransaccionResponse>;

export async function cancelarTransaccion(
  input: CancelarTransaccionRequest,
): Promise<CancelarTransaccionResponse> {
  const req = CancelarTransaccionRequest.parse(input);
  await delay(MOCK_LATENCY_MS);

  const db = getDb();
  const op = db.operaciones.find((o) => o.id === req.operacionId);
  if (!op) throw new ApiError(`Operación ${req.operacionId} no encontrada`, 'OP_NOT_FOUND');
  if (op.anmat.estado === 'confirmada') {
    throw new ApiError('No se puede cancelar: la contraparte ya confirmó', 'OP_CONFIRMED');
  }
  if (op.anmat.estado === 'cancelada') {
    throw new ApiError('La operación ya fue cancelada', 'OP_ALREADY_CANCELLED');
  }

  const ahora = new Date().toISOString();

  // Revertir series — vuelven a en_poder en la ubicación de origen.
  // FIX CR-3: incluye 'destruida' para que las destrucciones informadas también reviertan.
  for (const sid of op.seriesIds) {
    const serie = db.series.find((s) => s.id === sid);
    if (!serie) continue;
    if (
      serie.tenencia === 'en_transito' ||
      serie.tenencia === 'entregada' ||
      serie.tenencia === 'destruida'
    ) {
      serie.tenencia = 'en_poder';
      serie.ubicacionActualId = op.ubicacionId;
    }
    db.movimientos.push({
      id: nextMovId(),
      serieId: sid,
      operacionId: op.id,
      tipoEvento: 'cancelacion',
      fecha: ahora,
      ubicacionId: op.ubicacionId,
      usuarioId: req.usuarioId,
      codigoTransaccion: null,
      eventoAnmatId: null,
      detalle: `Cancelación · razón: ${req.razon}`,
    });
  }

  // Marcar operación como cancelada
  op.anmat.estado = 'cancelada';
  op.anmat.mensajes.push({
    ts: ahora,
    tipo: 'request',
    body: `cancelarTransaccion · razón: ${req.razon}`,
  });
  op.fechaCancelacion = ahora;

  // Devolver el remito al pool de pendientes (re-despacho posible)
  if (op.remitoNumero) {
    const rem = db.remitos.find((r) => r.numero === op.remitoNumero);
    if (rem) rem.estadoEnPlataforma = 'pendiente_trazar';
  }

  // (v13) Log del POST de cancelación al SNT
  logApi({
    sistema: 'snt',
    metodo: 'POST',
    endpoint: '/snt/cancelarTransaccion',
    status: 200,
    operacionId: op.id,
    requestBody: { codigoTransaccion: op.anmat.codigoTransaccion, motivo: req.razon },
    responseBody: { estado: 'cancelada', anuladoEnSnt: true },
  });

  return CancelarTransaccionResponse.parse({ operacion: op });
}
